










































































import { badgeFilters } from '@/libs/domains/catalog/badges/filters'
import { combineFilters } from '@/libs/tables/filters'
import { computed, defineComponent, reactive, ref } from '@vue/composition-api'
import { debouncedWatch } from '@vueuse/core'
import { gridTemplate } from '@/libs/tables/grid'
import { isEmptyArray } from '@/libs/arrays'
import { useApi } from '@/composable/useApi'
import { useCrudChecker } from '@/composable/useCrudChecker'
import { useI18n } from 'vue-i18n-bridge'
import DeletableChips from '@/components/common/DeletableChips.vue'
import FiltersChips from '@/components/tables/filters/FiltersChips.vue'
import FiltersGridDrawer from '@/components/tables/filters/FiltersGridDrawer.vue'
import ResourceGrid from '@/components/tables/ResourceGrid.vue'
import type { Badge, BadgeFilter, BadgeGridRequest } from '@futureecom/futureecom-js/dist/services/catalog-service'
import type { FilterDefinition, FilterTextDefinition } from '@/types/FilterDefinitions'
import type { Grid } from '@/types/Grids'
import type { PropType } from '@vue/composition-api'
import type { ResourceGridOptions } from '@/types/Grids'
import type { ValidationErrors } from '@/types/validator'

export default defineComponent({
  name: 'SelectableBadges',

  components: { FiltersGridDrawer, FiltersChips, DeletableChips, ResourceGrid },

  props: {
    value: {
      type: Object as PropType<{ badge_ids?: string[]; badge_id?: string; valueKeys?: string[] }>,
      default: undefined
    },
    items: {
      type: Array as PropType<Badge[]>,
      default: undefined
    },
    fetchByFilterKey: {
      type: String as PropType<keyof Badge>,
      default: 'id'
    },
    searchKey: {
      type: [] as PropType<BadgeFilter>,
      default: 'code'
    },
    gridOptions: {
      type: Object as PropType<ResourceGridOptions<BadgeFilter>>,
      default: undefined
    },
    requestOptions: {
      type: Object as PropType<BadgeGridRequest>,
      default: undefined
    },
    showChips: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    errors: {
      type: Object as PropType<ValidationErrors>,
      default: () => ({})
    }
  },

  emits: {
    'input': (_: { badge_ids?: string[] | null; badge_id?: string | null }) => true,
    'select:keys': (_: unknown[]) => true,
    'select': (_: Badge[]) => true
  },

  setup(props, { emit }) {
    const { canRead } = useCrudChecker('catalog\\badge')

    const { t } = useI18n()

    const searchVal = ref('')
    const currentParams = ref<BadgeGridRequest>()
    const isFilterOpen = ref(false)

    const currentItems = ref<Badge[]>(props.items || [])
    const grid = reactive<Grid>({ ...gridTemplate() })

    const api = useApi()

    const filters = computed((): FilterDefinition[] => {
      const searchFilter: FilterTextDefinition<BadgeFilter> = { code: props.searchKey, label: '', type: 'text' }
      const combined = combineFilters<BadgeFilter>(badgeFilters, props.gridOptions?.filters || [], [searchFilter])
      return combined.map((filter) => ({ ...filter, label: t(`${filter.label}`) }))
    })

    const fetchSelectedItems = () => {
      const values = (props.value?.valueKeys || props.value?.badge_ids || [props.value?.badge_id || '']).filter(Boolean)

      if (!props.items && !isEmptyArray(values)) {
        api.catalog
          .badges({ filter: { [props.fetchByFilterKey]: values }, perPage: -1 })
          .then((val) => (currentItems.value = val.data))
          .catch((err) => console.error(err))
      }
    }

    const reloadCollection = (params: BadgeGridRequest): void => {
      currentParams.value = {
        ...params,
        ...props.requestOptions,
        filter: { ...params.filter, ...props.requestOptions?.filter }
      }

      grid.loading = true
      api.catalog
        .badges(currentParams.value)
        .then((val) => {
          grid.data = val.data
          grid.meta = val.meta
        })
        .finally(() => (grid.loading = false))
    }

    const onItemSelect = (items: Badge[]) => {
      currentItems.value = items.filter((item) => props.fetchByFilterKey in item)

      if (!props.multiple) {
        const lastSelectedItem = items.at(-1)
        currentItems.value = lastSelectedItem ? [lastSelectedItem] : []
      }

      const ids = currentItems.value.map((badge) => badge.id)
      const valueToEmit = props.multiple ? { badge_ids: !isEmptyArray(ids) ? ids : [] } : { badge_id: ids[0] || null }

      const valueKeys = currentItems.value.map((item) => item[props.fetchByFilterKey]).filter(Boolean)

      emit('input', { ...props.value, ...valueToEmit })
      emit('select:keys', valueKeys)
      emit('select', items)
    }

    debouncedWatch(
      searchVal,
      () => {
        const params = {
          ...currentParams.value,
          filter: { ...currentParams.value?.filter, [props.searchKey]: searchVal.value || undefined }
        }

        reloadCollection(params)
      },
      { debounce: 700 }
    )

    if (canRead.value) {
      fetchSelectedItems()
    }

    const headers = computed(() => {
      const defaultHeaders = [
        { text: t('Code'), value: 'code' },
        { text: t('Text'), value: 'text' }
      ]
      return props.gridOptions?.headers || defaultHeaders
    })

    return {
      canRead,
      searchVal,
      currentParams,
      currentItems,
      grid,
      filters,
      isFilterOpen,
      headers,
      onItemSelect,
      reloadCollection
    }
  }
})
